import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Play, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock, SkipForward,
  Activity, ChevronRight, Loader2, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { AutonomyCard } from '@/components/AutonomyCard';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const STEP_ICON = { ok: CheckCircle2, error: XCircle, timeout: Clock, skipped: SkipForward, running: Loader2 };
const STEP_COLOR = { ok: C.green, error: C.red, timeout: C.red, skipped: C.slate, running: C.blue };
const CANARY_STEPS = ['submission', 'score', 'bind', 'schedule_pdf', 'fnol', 'reserve', 'settle', 'renewal_gates'];
const VERDICT = { helped: C.green, no_change: C.slate, hurt: C.red, not_measurable: C.slate };

const num = n => (n === null || n === undefined ? '—' : typeof n === 'number' ? n.toLocaleString() : String(n));

function Stat({ label, value, color, sub, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-2xl font-bold leading-none" style={{ color }}>{value}</p>
      <p className="text-[10px] uppercase mt-1 leading-tight" style={{ color: C.slate }}>{label}</p>
      {sub && <p className="text-[10px]" style={{ color: C.slate }}>{sub}</p>}
    </div>
  );
}

export default function OpsConsolePage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [props_, setProps] = useState(null);
  const [busy, setBusy] = useState('');
  const [notes, setNotes] = useState({});

  const load = useCallback(() => {
    Promise.all([api.get('/ops/today'), api.get('/ops/proposals', { params: { limit: 30 } })])
      .then(([a, b]) => { setData(a.data); setProps(b.data); })
      .catch(() => toast.error('Could not load the operations console'));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const runNow = async (only) => {
    setBusy(only ? `step:${only}` : 'run');
    try {
      const { data: r } = await api.post('/ops/run', { force: true, only: only ? [only] : null });
      toast.success(r.skipped ? r.reason : `Day run ${r.status} in ${r.duration_sec}s — score ${r.score?.total}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Run failed'); }
    setBusy('');
  };

  const decide = async (id, approve) => {
    setBusy(`p:${id}`);
    try {
      const { data: r } = await api.post(`/ops/proposals/${id}/decide`, { approve, note: notes[id] || '' });
      toast.success(r.note || `Proposal ${r.status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record that'); }
    setBusy('');
  };

  if (!data) return <p className="text-sm" style={{ color: C.slate }}>Loading the day run…</p>;
  const run = data.run || {};
  const score = run.score || {};
  const canary = data.canary || {};
  const needs = data.needs_human || { items: [] };
  const open = (props_?.proposals || []).filter(p => p.status === 'open');
  const decided = (props_?.proposals || []).filter(p => p.status !== 'open');

  const CANARY_INBOUND = canary.inbound || null;

  return (
    <div className="space-y-4" data-testid="ops-console">
      <AutonomyCard />

      {/* Day run */}
      <Card className="rounded-sm border-l-4 bg-white p-5" data-testid="ops-day-run"
        style={{ borderLeftColor: run.status === 'ok' ? C.green : run.status === 'degraded' ? C.amber : C.red }}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
              Day run · {run.run_date || '—'}
            </h3>
            <p className="text-xs mt-1" style={{ color: C.slate }}>
              The operating cycle runs at {String(data.schedule?.hour_local).padStart(2, '0')}:00 {data.schedule?.timezone} and leaves this receipt.
              Started by {run.run_by || '—'}{run.duration_sec ? ` · finished in ${run.duration_sec}s` : ''}.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Stat label={`operating score · ${score.band || '—'}`} value={score.total ?? '—'} testid="ops-score"
              color={(score.total ?? 0) >= 90 ? C.green : (score.total ?? 0) >= 75 ? C.blue : C.amber} />
            <div className="flex gap-1.5">
              <button onClick={load} className="p-2 rounded hover:bg-slate-50" style={{ color: C.slate }} data-testid="ops-refresh">
                <RefreshCw className="w-4 h-4" />
              </button>
              {isAdmin && (
                <Button size="sm" onClick={() => runNow(null)} disabled={!!busy} className="rounded-sm text-xs"
                  style={{ background: C.navy }} data-testid="ops-run-now">
                  {busy === 'run' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1" /> Run the cycle now</>}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-5 flex-wrap mt-4 mb-3">
          <Stat label="steps ok" value={`${(run.steps || []).filter(s => s.status === 'ok').length}/${(run.steps || []).filter(s => s.status !== 'skipped').length}`} color={C.navy} testid="ops-steps-ok" />
          <Stat label="classes walked end to end" value={`${canary.passed ?? 0}/${canary.total ?? 0}`} testid="ops-canary-pass"
            color={(canary.failed ?? 0) === 0 ? C.green : C.red} sub={canary.pass_rate_pct != null ? `${canary.pass_rate_pct}% pass` : null} />
          <Stat label="not the machine's call" value={needs.total ?? 0} color={(needs.total ?? 0) ? C.amber : C.green} testid="ops-needs-human-count" />
          <Stat label="proposals open" value={open.length} color={open.length ? C.blue : C.slate} testid="ops-proposals-open" />
        </div>

        <table className="w-full text-xs" data-testid="ops-steps-table">
          <tbody>
            {(run.steps || []).map(s => {
              const Icon = STEP_ICON[s.status] || Activity;
              return (
                <tr key={s.id} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`ops-step-${s.id}`}>
                  <td className="py-1.5 pr-2 w-6"><Icon className="w-3.5 h-3.5" style={{ color: STEP_COLOR[s.status] || C.slate }} /></td>
                  <td className="py-1.5 pr-3 font-medium w-64" style={{ color: C.navy }}>
                    {s.title}
                    <span className="block text-[10px] font-normal" style={{ color: C.slate }}>{s.owner} · {s.cadence}{s.writes ? '' : ' · read only'}</span>
                  </td>
                  <td className="py-1.5 pr-3 text-[11px]" style={{ color: s.status === 'error' || s.status === 'timeout' ? '#B03230' : C.slate }}>
                    {s.status === 'skipped' ? s.skipped_reason
                      : s.error ? s.error
                        : Object.entries(s.produced || {}).filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length))
                          .map(([k, v]) => `${k.replace(/_/g, ' ')} ${Array.isArray(v) ? v.join(', ') : num(v)}`).join(' · ') || '—'}
                    {s.note && <span className="block text-[10px] italic">{s.note}</span>}
                  </td>
                  <td className="py-1.5 text-right text-[10px] whitespace-nowrap" style={{ color: C.slate }}>
                    {s.ms != null ? `${s.ms}ms` : ''}
                    {isAdmin && s.status !== 'skipped' && (
                      <button onClick={() => runNow(s.id)} disabled={!!busy} className="ml-2 underline" data-testid={`ops-rerun-${s.id}`}>
                        {busy === `step:${s.id}` ? '…' : 'rerun'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Canary matrix */}
      <Card className="rounded-sm bg-white p-5" data-testid="ops-canary-card"
        style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
            Canary — every class walked end to end
          </h3>
          <span className="text-[10px]" style={{ color: C.slate }}>
            {canary.at ? `last walk ${String(canary.at).slice(0, 16).replace('T', ' ')} · ${canary.duration_sec}s · ${canary.steps_run} steps` : 'not yet run'}
          </span>
        </div>
        <p className="text-xs mb-3" style={{ color: C.slate }}>
          A synthetic risk walks the real functions for each class — quote, bind through the gates, schedule PDF, FNOL, reserve, settle, renewal gates — then deletes itself. Whatever breaks, breaks here first.
        </p>
        {CANARY_INBOUND && (
          <div className="mb-3 p-2.5 rounded-sm" data-testid="canary-inbound"
            style={{ background: CANARY_INBOUND.passed ? 'rgba(29,158,117,0.07)' : 'rgba(226,75,74,0.08)' }}>
            <p className="text-[11px] font-semibold mb-1" style={{ color: CANARY_INBOUND.passed ? '#14795A' : '#B03230' }}>
              Intake rail — a broker's email walked end to end {CANARY_INBOUND.passed ? 'and came out as a priced submission' : `— FAILED at ${CANARY_INBOUND.failed_at}`}
            </p>
            <div className="flex gap-4 flex-wrap">
              {(CANARY_INBOUND.steps || []).map(s => (
                <span key={s.step} className="text-[10px]" data-testid={`canary-inbound-${s.step}`} style={{ color: C.slate }}>
                  <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: s.status === 'pass' ? C.green : C.red }} />
                  <b style={{ color: C.navy }}>{s.step.replace(/_/g, ' ')}</b> — {s.detail}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ops-canary-matrix">
            <thead>
              <tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
                <th className="py-1 pr-3">Class</th>
                {CANARY_STEPS.map(s => <th key={s} className="py-1 px-1 text-center">{s.replace(/_/g, ' ')}</th>)}
                <th className="py-1 pl-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {(canary.classes || []).map(c => {
                const byStep = Object.fromEntries((c.steps || []).map(s => [s.step, s]));
                return (
                  <tr key={c.class_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`canary-row-${c.class_id}`}>
                    <td className="py-1.5 pr-3 font-medium" style={{ color: c.passed ? C.navy : C.red }}>{c.class_name}</td>
                    {CANARY_STEPS.map(sid => {
                      const st = byStep[sid];
                      const col = !st ? C.slate : st.status === 'pass' ? C.green : st.status === 'fail' ? C.red : C.amber;
                      return (
                        <td key={sid} className="py-1.5 px-1 text-center" title={st ? `${st.status} — ${st.detail || ''} (${st.ms}ms)` : 'not run'}>
                          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: col }} data-testid={`canary-${c.class_id}-${sid}`} />
                        </td>
                      );
                    })}
                    <td className="py-1.5 pl-2 text-[10px]" style={{ color: c.passed ? C.slate : '#B03230' }}>
                      {c.passed ? (byStep.bind?.detail || '').slice(0, 70) : `stopped at ${c.failed_at}: ${(byStep[c.failed_at]?.detail || '').slice(0, 70)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Not the machine's call */}
      <Card className="rounded-sm bg-white p-5" data-testid="ops-needs-human"
        style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${(needs.total ?? 0) ? C.amber : C.green}` }}>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-1" style={{ color: C.navy }}>Not the machine's call</h3>
        <p className="text-xs mb-3" style={{ color: C.slate }}>{needs.note}</p>
        {needs.items?.length ? (
          <div className="space-y-1.5">
            {needs.items.map(i => (
              <a key={i.kind} href={i.where} className="flex items-center gap-2 text-xs hover:underline" data-testid={`needs-${i.kind}`}>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: 'rgba(232,160,32,0.12)', color: '#B27516' }}>{i.count}</span>
                <span style={{ color: C.navy }}>{i.detail}</span>
                <ChevronRight className="w-3 h-3" style={{ color: C.slate }} />
              </a>
            ))}
          </div>
        ) : <p className="text-xs" style={{ color: C.green }} data-testid="needs-human-empty">Nothing is waiting on you.</p>}
      </Card>

      {/* Improvement proposals */}
      <Card className="rounded-sm bg-white p-5" data-testid="ops-proposals-card"
        style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
            Improvement proposals
          </h3>
          <span className="text-[10px]" style={{ color: C.slate }}>
            measured {props_?.measure_after_days} days after any change · verdicts so far:{' '}
            {Object.entries(props_?.verdicts || {}).map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`).join(', ') || 'none yet'}
          </span>
        </div>
        <p className="text-xs mb-3" style={{ color: C.slate }}>
          Operations run themselves, and so do the parameters they run on: a routing band or a rate move inside the cap
          is applied by the adjudicator and comes back with whether the number actually moved. Anything above the cap —
          and policy wording, always — waits for you.
        </p>
        {open.length === 0 && <p className="text-xs mb-3" style={{ color: C.green }} data-testid="proposals-empty">No proposals open — anything raised has already been applied, or is on the build backlog.</p>}
        <div className="space-y-2">
          {open.map(p => (
            <div key={p.proposal_id} className="p-2.5 rounded-sm" style={{ background: '#F4F7FA' }} data-testid={`proposal-${p.proposal_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[18rem]">
                  <p className="text-xs font-semibold" style={{ color: C.navy }}>
                    <span className="px-1.5 py-0.5 rounded text-[10px] mr-2" style={{ background: 'rgba(26,76,124,0.1)', color: C.blue }}>{p.kind.replace(/_/g, ' ')}</span>
                    {p.title}
                  </p>
                  <p className="text-[11px] mt-1" style={{ color: C.slate }}>{p.expected_effect}</p>
                  <p className="text-[10px] mt-1 font-mono" style={{ color: C.slate }}>
                    {Object.entries(p.evidence || {}).map(([k, v]) => `${k}: ${typeof v === 'number' ? num(v) : String(v).slice(0, 90)}`).join(' · ')}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex flex-col gap-1.5 items-end">
                    <Input value={notes[p.proposal_id] || ''} onChange={e => setNotes(n => ({ ...n, [p.proposal_id]: e.target.value }))}
                      placeholder="note (required to reject)" className="h-7 text-xs rounded-sm w-56" data-testid={`proposal-note-${p.proposal_id}`} />
                    <div className="flex gap-1.5">
                      <Button size="sm" disabled={!!busy} onClick={() => decide(p.proposal_id, true)}
                        className="h-7 text-[11px] rounded-sm" style={{ background: C.green }} data-testid={`proposal-approve-${p.proposal_id}`}>
                        {busy === `p:${p.proposal_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Approve & apply'}
                      </Button>
                      <Button size="sm" variant="outline" disabled={!!busy} onClick={() => decide(p.proposal_id, false)}
                        className="h-7 text-[11px] rounded-sm" data-testid={`proposal-reject-${p.proposal_id}`}>Reject</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {decided.length > 0 && (
          <table className="w-full text-xs mt-3" data-testid="ops-proposal-history">
            <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
              <th className="py-1 pr-2">Change</th><th className="py-1 pr-2">Status</th><th className="py-1 pr-2">Decided</th><th className="py-1">Did it help?</th>
            </tr></thead>
            <tbody>
              {decided.slice(0, 12).map(p => (
                <tr key={p.proposal_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                  <td className="py-1.5 pr-2" style={{ color: C.navy }}>{p.title}</td>
                  <td className="py-1.5 pr-2" style={{ color: p.status === 'rejected' ? C.slate : C.blue }}>{p.status.replace(/_/g, ' ')}</td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{p.decided_by || 'governor'} · {String(p.decided_at || p.applied_at || '').slice(0, 10)}</td>
                  <td className="py-1.5 text-[11px]" style={{ color: VERDICT[p.measure?.verdict] || C.slate }}>
                    {p.measure?.verdict
                      ? <><TrendingUp className="w-3 h-3 inline mr-1" />{p.measure.statement}</>
                      : p.measure?.after_date ? `measuring — due ${p.measure.after_date}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
